import { createApp } from 'vue'
import axios from 'axios'
import App from './App.vue'
import router from './router'
import vuetify from '@plugins/vuetify.js'
import AppState, { sessionState, globalState, snackState } from '@lib/state.js'
import UtilityPlugin, { useUtility } from '@highq/frontend-utility'
import MwCompatibilityCheckerPlugin from '@highq/mw-compatibility-checker'
import packageJson from '../package.json' with { type: 'json' }
import { connectKeycloak } from '@lib/keycloak.js'
import { middlewareConnector } from '@middleware-connector'
import '@/assets/fonts/fonts.css'
import { DateTime } from 'luxon'

const vueApp = createApp(App)

/**
 * (asynchronous) function that does the very basic setup
 * before the app can be "run()"
 * mainly handles provisions and configuration calls that
 * are needed for the app to function properly or at all
 */
const setup = async () => {
    const config = await middlewareConnector.getConfiguration()

    Object.assign(globalState, {
        systemConfig: config.systemConfig,
    })

    const requiredFields = await middlewareConnector.getRequiredFields()
    Object.assign(globalState, {
        requiredFields: requiredFields
    })

    // --- KEYCLOAK INSTANTIATION ---
    const keycloakInstance = await connectKeycloak(config.keycloak)

    // --- SESSION INSTANTIATION ---
    // after the keyloack auth is established, we can decide if we need a new Session
    // or if we can use the old one
    // keycloak provides an "auth_time" param in the parsed token
    // with that we can differntiate if the user just logged in, or if the keycloak auth session
    // was still active
    // if the session was still active, we will reuse the sessionState, if not we create a new one
    const loginAuthTime = keycloakInstance.tokenParsed.auth_time
    if (sessionState.authTime !== loginAuthTime) {
        sessionState.reinit(loginAuthTime)

        // init sessionState with fake corporation as a default
        const uuidCorporation = keycloakInstance.tokenParsed.corporations[0]
        /** @type {Corporation} */
        const fakeCorporation = {
            number: '',
            uuid: uuidCorporation,
            name: ''
        }
        sessionState.corporation = fakeCorporation
    }

    // --- --- --- --- --- --- ---
    // --- AXIOS SETUP ---
    // we set up axios to e.g. send headers with every request like the KC token
    configureAxios(keycloakInstance, globalState)
    // --- --- --- --- --- --- ---

    const corporation = await middlewareConnector.getCorporation(sessionState.corporation.uuid)

    sessionState.corporation = {
        number: corporation.corporationNumber,
        uuid: corporation.uuidCustomer,
        name: corporation.name,
        corporationType: corporation.corporationType
    }

    const baseData = await middlewareConnector.getBaseData()
    Object.assign(globalState, { baseData: baseData })

    if (!sessionState.schoolYear) {
        sessionState.schoolYear = getCurrentSchoolYear(globalState.baseData.schoolYears)
    }
}


/**
 * 1. takes a responsive keycloak state to send the current token
 *    with every request.
 *    this does not work when the reference of the property is passed directly
 *    -> "keycloak.token" as param
 *        since the reactivity is not deep!
 *
 * 2. keeps timers for every request that trigger a loading State if the request
 *    takes too long. Adds a timer for every request, removes a time for every response in time
 *
 * @param {import('keycloak-js').default} keycloak keycloak instance
 */
function configureAxios(keycloak, globalResponsiveState, timeBeforeUiBlockInMs = 2000) {
    const loadingTimerIds = []

    axios.interceptors.request.use(config => {
        config.headers['Authorization'] = 'Bearer ' + keycloak.token
        config.headers['uuidCorporation'] = sessionState.corporation.uuid
        config.headers['isSchool'] = true

        const newTimerId = setTimeout(() => {
            globalResponsiveState.isLoading = true
        }, timeBeforeUiBlockInMs)

        loadingTimerIds.push(newTimerId)

        // pass timeId to config, so that it is available in the response
        config.timerId = newTimerId
        // also pass time of start so wen can check if the timer triggered
        config.timerStart = Date.now()

        return config
    })

    axios.interceptors.response.use(
        response => {
            responseTimerHandler(response)
            return response
        },
        error => {
            if (error.response) {
                responseTimerHandler(error.response)
            } else {
                responseTimerHandler(error)
            }
            throw error
        }
    )

    // handles timers from resposnes
    const responseTimerHandler = (response) => {
        // cancel the timer
        // and if triggered reset the loading state
        const timerIdToCancel = response.config.timerId
        const timerIndex = loadingTimerIds.indexOf(timerIdToCancel)
        const timerStart = response.config.timerStart
        const now = Date.now()

        const timerTriggered = (now - timerStart) > timeBeforeUiBlockInMs

        if (timerTriggered) {
            // if the timer was triggered, reset the loading screen
            globalResponsiveState.isLoading = false
        }

        // remove the timer id from the list and clear timeout
        loadingTimerIds.splice(timerIndex, 1)
        clearTimeout(timerIdToCancel)

        // follow the response chain
    }
}


const getCurrentSchoolYear = (schoolYears) => {
    if (!schoolYears) {
        return undefined
    }

    const now = DateTime.now()
    const currentSchoolYear = schoolYears.find(schoolYear => DateTime.fromISO(schoolYear.startDate) <= now && DateTime.fromISO(schoolYear.endDate).endOf('day') >= now)

    if (currentSchoolYear) {
        return currentSchoolYear
    }

    return schoolYears[0]
}

/**
 * function that is called after the basic "setup()" is done
 * represents the acutal app start
 */
const run = () => {
    // --- REGISTER COMPLETE PLUGINS ---
    vueApp.use(router)
    vueApp.use(UtilityPlugin)
    vueApp.use(MwCompatibilityCheckerPlugin, {
        versionEndpointUrl: useUtility().middlewareUrl + '/info/version',
        compatibleSemverVersion: packageJson.compatibleMiddlewareVersion
    })
    vueApp.use(vuetify)
    vueApp.use(AppState)
    // --- --- --- --- --- --- ---
    Object.assign(snackState, {
        timeout: 3000,
        position: 'bottom',
        show: false,
    })
    // --- MOUNT THE APP --- (actual app start)
    vueApp.mount('#app')
}

// >--< >--< >--< >--< >--< >--< >--< >--<
// >--< APP START  PROMISE CHAIN >--< >--<
// >--< >--< >--< >--< >--< >--< >--< >--<
// setup needs to be finished before we can run the app
setup()
.then(() => {
    run()
})
// >--< >--< >--< >--< >--< >--< >--< >--<